import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import Swal from "sweetalert2";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import Loading from "components/Loading";
import Select2Field from "components/Select2Field";
import EstablishmentMapPicker from "components/EstablishmentMapPicker";
import TablePaginationBar from "components/TablePaginationBar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useTranslate } from "utils/i18n";
import {
  figmaTokens,
  directoryTableHeaderCellSx,
  directoryTableHeadStickyOverridesSx,
  directoryTableBodyCellDefaultsSx,
} from "assets/theme/designTokens";
import { useSoftUIController } from "context";
import { isObserver, isSuperAdmin } from "assistant/userRole";
import {
  archiveEstablishment,
  createEstablishment,
  getEstablishment,
  getEstablishmentDeletionImpact,
  getEstablishments,
  getWebDepartments,
  getWebProvinces,
  getWebSubPrefectures,
  updateEstablishment,
} from "services/establishmentService";
import {
  establishmentStatusOptions,
  establishmentTypeOptions,
} from "services/establishmentFilters";

const dialogPaperProps = {
  sx: {
    borderRadius: "16px",
    overflow: "hidden",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    boxShadow: "0 24px 64px rgba(15, 23, 42, 0.14)",
  },
};

const formFieldLabelSx = {
  display: "block",
  mb: 0.75,
  fontWeight: 700,
  fontSize: "0.68rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: figmaTokens.textMuted,
};

function DetailRow({ label, value }) {
  return (
    <Grid item xs={12} sm={6}>
      <SoftTypography variant="caption" sx={formFieldLabelSx}>
        {label}
      </SoftTypography>
      <SoftTypography variant="body2" sx={{ color: figmaTokens.textPrimary, fontWeight: 600 }}>
        {value || "—"}
      </SoftTypography>
    </Grid>
  );
}

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function EstablishmentsPage() {
  const [controller] = useSoftUIController();
  const readOnly = isObserver(controller.user);
  const superAdminView = isSuperAdmin(controller.user);
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [provOpts, setProvOpts] = useState([]);
  const [deptOpts, setDeptOpts] = useState([]);
  const [subpOpts, setSubpOpts] = useState([]);

  const requiredMessage = t("validation.required", "Required");

  const fetchEstablishments = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getEstablishments({
        pageNumber: page,
        pageSize,
        search: appliedSearch,
        type: typeFilter,
        status: statusFilter,
      });
      setRows(result.items);
      setTotalRecords(result.pagination.totalRecords);
      setTotalPages(result.pagination.totalPages);
    } catch (err) {
      console.error("Fetch establishments error", {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
        url: "/api/web/establishments",
      });
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("establishments.load_error", "Failed to load establishments.")
      );
      setRows([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, page, pageSize, statusFilter, t, typeFilter]);

  useEffect(() => {
    fetchEstablishments();
  }, [fetchEstablishments]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setAppliedSearch(searchQuery.trim());
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const typeFilterOptions = establishmentTypeOptions(t);
  const statusFilterOptions = establishmentStatusOptions(t);

  const fetchDepartments = async (provid) => {
    if (!provid) {
      setDeptOpts([]);
      return;
    }
    try {
      setDeptOpts(await getWebDepartments(provid));
    } catch (err) {
      console.error("Fetch departments error", err);
      setDeptOpts([]);
    }
  };

  const fetchSubprefectures = async (dptid) => {
    if (!dptid) {
      setSubpOpts([]);
      return;
    }
    try {
      setSubpOpts(await getWebSubPrefectures(dptid));
    } catch (err) {
      console.error("Fetch subprefectures error", err);
      setSubpOpts([]);
    }
  };

  const loadProvinces = async () => {
    try {
      setProvOpts(await getWebProvinces());
    } catch (err) {
      console.error("Fetch provinces error", err);
      setProvOpts([]);
    }
  };

  const formik = useFormik({
    initialValues: {
      province: "",
      department: "",
      subprefecture: "",
      spId: "",
      name: "",
      code: "",
      creationDate: `${new Date().getFullYear()}-01-01`,
      latitude: "",
      longitude: "",
      status: "",
      type: "",
    },
    validate: (values) => {
      const errors = {};
      if (!values.name?.trim()) errors.name = requiredMessage;
      if (!values.spId) errors.subprefecture = requiredMessage;
      if (!values.type?.trim()) errors.type = requiredMessage;
      if (!values.status?.trim()) errors.status = requiredMessage;
      if (!values.creationDate?.trim()) errors.creationDate = requiredMessage;
      const lat = parseFloat(values.latitude);
      const lng = parseFloat(values.longitude);
      if (!Number.isFinite(lat)) errors.latitude = requiredMessage;
      if (!Number.isFinite(lng)) errors.longitude = requiredMessage;
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      if (editingRow && !superAdminView) {
        setEditingRow(null);
        setFormOpen(false);
        return;
      }
      setLoading(true);
      try {
        const payload = {
          name: values.name.trim(),
          code: values.code.trim(),
          creationDate: values.creationDate.trim(),
          latitude: Number(values.latitude),
          longitude: Number(values.longitude),
          status: values.status.trim(),
          subPrefectureId: Number(values.spId),
          type: values.type.trim(),
        };
        if (editingRow) {
          await updateEstablishment(editingRow.id, {
            ...payload,
            rowVersion: editingRow.rowVersion,
          });
        } else {
          await createEstablishment(payload);
        }
        resetForm();
        setFormOpen(false);
        setEditingRow(null);
        setDeptOpts([]);
        setSubpOpts([]);
        setPage(1);
        await fetchEstablishments();
        Swal.fire({
          icon: "success",
          title: t("establishments.create_success_title", "Saved"),
          text: editingRow
            ? t("establishments.update_success", "Establishment updated successfully.")
            : t("establishments.create_success", "Establishment created successfully."),
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: t("establishments.error_title", "Error"),
          text:
            err?.response?.data?.message ||
            err?.response?.data?.title ||
            (editingRow
              ? t("establishments.update_error", "Failed to update establishment.")
              : t("establishments.create_error", "Failed to create establishment.")),
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const openCreate = () => {
    setEditingRow(null);
    formik.resetForm();
    setDeptOpts([]);
    setSubpOpts([]);
    loadProvinces();
    setFormOpen(true);
  };

  const openEdit = async (row) => {
    if (!superAdminView) return;
    setLoading(true);
    try {
      const detail = await getEstablishment(row.id);
      const [provinces, departments, subPrefectures] = await Promise.all([
        getWebProvinces(),
        detail.provinceId ? getWebDepartments(detail.provinceId) : Promise.resolve([]),
        detail.departmentId ? getWebSubPrefectures(detail.departmentId) : Promise.resolve([]),
      ]);
      setProvOpts(provinces);
      setDeptOpts(departments);
      setSubpOpts(subPrefectures);
      setEditingRow(detail);
      formik.setValues({
        province: detail.provinceId || "",
        department: detail.departmentId || "",
        subprefecture: detail.spId || "",
        spId: detail.spId || "",
        name: detail.name || "",
        code: detail.code || "",
        creationDate: detail.creationDate ? String(detail.creationDate).slice(0, 10) : "",
        latitude: detail.latitude ?? "",
        longitude: detail.longitude ?? "",
        status: detail.status || "",
        type: detail.type || "",
      });
      setFormOpen(true);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("establishments.error_title", "Error"),
        text:
          err?.response?.data?.message ||
          t("establishments.detail_error", "Failed to load establishment details."),
      });
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (row) => {
    setDetailRow(row);
    setDetailOpen(true);
    try {
      setDetailRow(await getEstablishment(row.id));
    } catch (err) {
      console.error("Fetch establishment details error", err);
    }
  };

  const handleDelete = async (row) => {
    if (!superAdminView) return;
    setLoading(true);
    let impact;
    try {
      impact = await getEstablishmentDeletionImpact(row.id);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("establishments.error_title", "Error"),
        text:
          err?.response?.data?.message ||
          t("establishments.impact_error", "Could not verify the deletion impact."),
      });
      return;
    } finally {
      setLoading(false);
    }

    const impactValue = (camel, pascal, fallback) => impact?.[camel] ?? impact?.[pascal] ?? fallback;
    const establishmentName = String(impactValue("name", "Name", row?.name ?? "")).trim();
    const referenceCode = String(impactValue("code", "Code", row?.code ?? row?.id ?? "")).trim();
    const assignmentCount = Number(impactValue("assignmentCount", "AssignmentCount", 0));
    const activeAssignmentCount = Number(
      impactValue("activeAssignmentCount", "ActiveAssignmentCount", 0)
    );
    const attendanceCount = Number(impactValue("attendanceCount", "AttendanceCount", 0));
    const nameConfirmation = establishmentName || referenceCode;
    const finalPhrase = `DELETE ${referenceCode || row.id}`;

    const warningResult = await Swal.fire({
      title: t("establishments.delete_confirm_title", "Archive establishment?"),
      text: t(
        "establishments.delete_warning_text",
        'You are about to archive "{{name}}" (code: {{code}}). Impact: {{assignments}} assignments ({{active}} active) and {{attendance}} attendance records.'
      )
        .replace("{{name}}", establishmentName || "—")
        .replace("{{code}}", referenceCode || "—")
        .replace("{{assignments}}", String(assignmentCount))
        .replace("{{active}}", String(activeAssignmentCount))
        .replace("{{attendance}}", String(attendanceCount)),
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: figmaTokens.textMuted,
      confirmButtonText: t("establishments.delete_continue", "Continue"),
      cancelButtonText: t("establishments.cancel", "Cancel"),
    });

    if (!warningResult.isConfirmed) return;

    const nameResult = await Swal.fire({
      title: t("establishments.delete_type_name_title", "Confirm the establishment"),
      text: t(
        "establishments.delete_type_name_text",
        'Type the exact establishment name: "{{name}}"'
      ).replace("{{name}}", nameConfirmation),
      input: "text",
      inputPlaceholder: nameConfirmation,
      inputAttributes: { autocomplete: "off", spellcheck: "false" },
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: figmaTokens.textMuted,
      confirmButtonText: t("establishments.delete_verify", "Verify"),
      cancelButtonText: t("establishments.cancel", "Cancel"),
      inputValidator: (value) =>
        value === nameConfirmation
          ? undefined
          : t("establishments.delete_name_mismatch", "The establishment name does not match exactly."),
    });

    if (!nameResult.isConfirmed) return;

    const reasonResult = await Swal.fire({
      title: t("establishments.delete_reason_title", "Reason for archiving"),
      text: t(
        "establishments.delete_reason_text",
        "Enter a clear reason. It will be stored in the establishment audit record."
      ),
      input: "textarea",
      inputPlaceholder: t("establishments.delete_reason_placeholder", "Reason"),
      inputAttributes: { maxlength: "500" },
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: figmaTokens.textMuted,
      confirmButtonText: t("establishments.delete_continue", "Continue"),
      cancelButtonText: t("establishments.cancel", "Cancel"),
      inputValidator: (value) =>
        value?.trim()
          ? undefined
          : t("establishments.delete_reason_required", "A reason is required."),
    });

    if (!reasonResult.isConfirmed) return;

    const finalResult = await Swal.fire({
      title: t("establishments.delete_final_title", "Final deletion confirmation"),
      text: t(
        "establishments.delete_final_text",
        'Type "{{phrase}}" to archive this establishment.'
      ).replace("{{phrase}}", finalPhrase),
      input: "text",
      inputPlaceholder: finalPhrase,
      inputAttributes: { autocomplete: "off", spellcheck: "false" },
      icon: "error",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: "#b91c1c",
      cancelButtonColor: figmaTokens.textMuted,
      confirmButtonText: t("establishments.delete_confirm_yes", "Archive establishment"),
      cancelButtonText: t("establishments.cancel", "Cancel"),
      inputValidator: (value) =>
        value === finalPhrase
          ? undefined
          : t("establishments.delete_phrase_mismatch", "The confirmation phrase does not match exactly."),
    });

    if (!finalResult.isConfirmed) return;

    setLoading(true);
    try {
      await archiveEstablishment(row.id, {
        rowVersion: impactValue("rowVersion", "RowVersion", row.rowVersion),
        reason: reasonResult.value.trim(),
        confirmation: finalPhrase,
      });
      if (rows.length === 1 && page > 1) setPage((current) => current - 1);
      await fetchEstablishments();
      Swal.fire({
        icon: "success",
        title: t("establishments.delete_success_title", "Archived"),
        text: t("establishments.delete_success", "Establishment archived successfully."),
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("establishments.error_title", "Error"),
        text:
          err?.response?.data?.message ||
          t("establishments.delete_error", "Failed to archive establishment."),
      });
    } finally {
      setLoading(false);
    }
  };

  const renderFieldError = (name) =>
    formik.touched[name] && formik.errors[name] ? (
      <SoftTypography variant="caption" color="error">
        {formik.errors[name]}
      </SoftTypography>
    ) : null;

  const listSummaryLabel =
    appliedSearch && totalRecords > 0
      ? t("establishments.search_results", "{{shown}} of {{total}} establishments")
          .replace("{{shown}}", String(rows.length))
          .replace("{{total}}", String(totalRecords))
      : totalRecords > 0
        ? t("establishments.loaded_count", "{{count}} establishments loaded").replace(
            "{{count}}",
            String(totalRecords)
          )
        : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && <Loading />}
      <SoftBox py={3} sx={{ bgcolor: figmaTokens.pageBg }}>
        <SoftBox mb={3}>
          <Card sx={{ borderRadius: "18px", overflow: "hidden", border: "none", boxShadow: "0 22px 56px rgba(15, 23, 42, 0.09)" }}>
            <SoftBox sx={{ height: 5, background: figmaTokens.gradientCSS }} />
            <SoftBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                p: { xs: 2.25, md: 3 },
              }}
            >
              <SoftBox sx={{ minWidth: 0, flex: "1 1 240px" }}>
                <SoftTypography variant="overline" sx={{ fontWeight: 800, letterSpacing: "0.14em", color: figmaTokens.textMuted, fontSize: "0.68rem" }}>
                  {t("establishments.page_kicker", "DIRECTORY")}
                </SoftTypography>
                <SoftTypography variant="h4" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
                  {t("establishments.title", "Establishments")}
                </SoftTypography>
                <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 1, maxWidth: 560 }}>
                  {t("establishments.page_hint", "Browse, create, and manage establishments.")}
                </SoftTypography>
              </SoftBox>
              {!readOnly && <SoftBox display="flex" flexWrap="wrap" gap={1.25}>
                {superAdminView && <Button
                  variant="outlined"
                  onClick={() => navigate("/parametres")}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "12px",
                    borderColor: figmaTokens.borderSubtle,
                    color: figmaTokens.textPrimary,
                  }}
                >
                  {t("establishments.import_csv", "Import CSV")}
                </Button>}
                <Button
                  variant="contained"
                  disableElevation
                  onClick={openCreate}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    px: 2.75,
                    py: 1.1,
                    borderRadius: "12px",
                    color: "#fff",
                    background: figmaTokens.gradientCSS,
                  }}
                >
                  {t("establishments.add", "Add establishment")}
                </Button>
              </SoftBox>}
            </SoftBox>
          </Card>
        </SoftBox>

        <Card variant="outlined" sx={{ borderRadius: "16px", border: `1px solid ${figmaTokens.borderSubtle}`, overflow: "hidden" }}>
          <SoftBox
            sx={{
              px: { xs: 2, md: 2.5 },
              py: 2,
              borderBottom: `1px solid ${figmaTokens.cardHeaderRule}`,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <SoftBox sx={{ minWidth: 0 }}>
              <SoftTypography variant="h6" fontWeight="bold">
                {t("establishments.table_title", "Establishment list")}
              </SoftTypography>
              {listSummaryLabel ? (
                <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, display: "block", mt: 0.5 }}>
                  {listSummaryLabel}
                </SoftTypography>
              ) : null}
            </SoftBox>
            <SoftBox display="flex" flexWrap="wrap" gap={1.25} sx={{ width: { xs: "100%", md: "auto" } }}>
              <SoftBox sx={{ width: { xs: "100%", sm: 180 } }}>
                <Select2Field
                  name="establishmentTypeFilter"
                  options={typeFilterOptions}
                  value={typeFilter}
                  onChange={(value) => {
                    setTypeFilter(value);
                    setPage(1);
                  }}
                />
              </SoftBox>
              <SoftBox sx={{ width: { xs: "100%", sm: 180 } }}>
                <Select2Field
                  name="establishmentStatusFilter"
                  options={statusFilterOptions}
                  value={statusFilter}
                  onChange={(value) => {
                    setStatusFilter(value);
                    setPage(1);
                  }}
                />
              </SoftBox>
              <SoftBox sx={{ width: { xs: "100%", sm: 320 } }}>
              <SoftInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("establishments.search_placeholder", "Search establishments…")}
                icon={{ component: <Icon>search</Icon>, direction: "left" }}
              />
              </SoftBox>
            </SoftBox>
          </SoftBox>

          {error ? (
            <SoftBox px={2.5} pb={2} pt={1}>
              <Alert severity="error">{error}</Alert>
            </SoftBox>
          ) : null}

          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
            <Table
              size="small"
              stickyHeader
              sx={{ minWidth: 900, ...directoryTableHeadStickyOverridesSx, ...directoryTableBodyCellDefaultsSx }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={directoryTableHeaderCellSx}>{t("establishments.col.name", "Name")}</TableCell>
                  <TableCell sx={directoryTableHeaderCellSx}>{t("establishments.col.code", "Code")}</TableCell>
                  <TableCell sx={directoryTableHeaderCellSx}>{t("establishments.col.type", "Type")}</TableCell>
                  <TableCell sx={directoryTableHeaderCellSx}>{t("establishments.col.status", "Status")}</TableCell>
                  <TableCell sx={directoryTableHeaderCellSx}>{t("establishments.col.subprefecture", "Sub-prefecture")}</TableCell>
                  <TableCell sx={directoryTableHeaderCellSx}>{t("establishments.col.coordinates", "Coordinates")}</TableCell>
                  <TableCell align="right" sx={directoryTableHeaderCellSx}>{t("establishments.actions", "Actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      {searchQuery.trim()
                        ? t("establishments.search_none", "No establishments match your search.")
                        : t("establishments.none", "No establishments found.")}
                    </TableCell>
                  </TableRow>
                ) : null}
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{row.name || "—"}</TableCell>
                    <TableCell>{row.code || "—"}</TableCell>
                    <TableCell>{row.type || "—"}</TableCell>
                    <TableCell>{row.status || "—"}</TableCell>
                    <TableCell>{row.spName || "—"}</TableCell>
                    <TableCell>
                      {row.latitude && row.longitude ? `${row.latitude}, ${row.longitude}` : "—"}
                    </TableCell>
                    <TableCell align="right">
                      <SoftBox display="flex" justifyContent="flex-end" gap={0.5}>
                        <IconButton size="small" onClick={() => openDetail(row)} sx={{ color: figmaTokens.textSecondary }}>
                          <Icon fontSize="small">visibility</Icon>
                        </IconButton>
                        {superAdminView && <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: figmaTokens.textSecondary }}>
                          <Icon fontSize="small">edit</Icon>
                        </IconButton>}
                        {superAdminView && <IconButton size="small" onClick={() => handleDelete(row)} sx={{ color: figmaTokens.textSecondary, "&:hover": { color: "#ef4444" } }}>
                          <Icon fontSize="small">delete</Icon>
                        </IconButton>}
                      </SoftBox>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePaginationBar
            page={page}
            totalCount={totalRecords}
            pageSize={pageSize}
            pageSizeOptions={[10, 25, 50, 100]}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(nextPageSize) => {
              setPageSize(nextPageSize);
              setPage(1);
            }}
            pageSizeLabel={t("pagination.rows_per_page", "Rows per page")}
          />
        </Card>
      </SoftBox>
      <Footer />

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="sm" PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ pr: 6 }}>
          {t("establishments.detail_title", "Establishment details")}
          <IconButton onClick={() => setDetailOpen(false)} sx={{ position: "absolute", right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailRow ? (
            <Grid container spacing={2}>
              <DetailRow label={t("establishments.col.name", "Name")} value={detailRow.name} />
              <DetailRow label={t("establishments.col.code", "Code")} value={detailRow.code} />
              <DetailRow label={t("establishments.col.type", "Type")} value={detailRow.type} />
              <DetailRow label={t("establishments.col.status", "Status")} value={detailRow.status} />
              <DetailRow label={t("establishments.col.subprefecture", "Sub-prefecture")} value={detailRow.spName} />
              <DetailRow label={t("establishments.field.creation_date", "Creation date")} value={detailRow.creationDate} />
              <DetailRow label={t("establishments.col.coordinates", "Coordinates")} value={`${detailRow.latitude || "—"}, ${detailRow.longitude || "—"}`} />
              <DetailRow label="ID" value={detailRow.id} />
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setDetailOpen(false)}>
            {t("establishments.close", "Close")}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {!readOnly && <Dialog open={formOpen} onClose={() => { setFormOpen(false); setEditingRow(null); }} fullWidth maxWidth="md" PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ pr: 6 }}>
          {editingRow
            ? t("establishments.edit_modal", "Edit establishment")
            : t("establishments.add_modal", "Add establishment")}
          <IconButton onClick={() => { setFormOpen(false); setEditingRow(null); }} sx={{ position: "absolute", right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <SoftTypography component="label" sx={formFieldLabelSx}>
                  {t("assignments.province", "Province")}
                </SoftTypography>
                <Select2Field
                  name="province"
                  options={[{ id: "", text: t("assignments.province_placeholder", "Province") }, ...provOpts]}
                  value={formik.values.province}
                  onChange={(val) => {
                    formik.setFieldValue("province", val);
                    formik.setFieldValue("department", "");
                    formik.setFieldValue("subprefecture", "");
                    formik.setFieldValue("spId", "");
                    setDeptOpts([]);
                    setSubpOpts([]);
                    if (val) fetchDepartments(val);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SoftTypography component="label" sx={formFieldLabelSx}>
                  {t("assignments.department", "Department")}
                </SoftTypography>
                <Select2Field
                  name="department"
                  options={[{ id: "", text: t("assignments.department_placeholder", "Department") }, ...deptOpts]}
                  value={formik.values.department}
                  onChange={(val) => {
                    formik.setFieldValue("department", val);
                    formik.setFieldValue("subprefecture", "");
                    formik.setFieldValue("spId", "");
                    setSubpOpts([]);
                    if (val) fetchSubprefectures(val);
                  }}
                  disabled={!formik.values.province}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SoftTypography component="label" sx={formFieldLabelSx}>
                  {t("assignments.subprefecture", "Sub-prefecture")}
                </SoftTypography>
                <Select2Field
                  name="subprefecture"
                  options={[{ id: "", text: t("assignments.subprefecture_placeholder", "Sub-prefecture") }, ...subpOpts]}
                  value={formik.values.subprefecture}
                  onChange={(val) => {
                    formik.setFieldValue("subprefecture", val);
                    formik.setFieldValue("spId", val);
                    formik.setFieldTouched("subprefecture", true);
                  }}
                  disabled={!formik.values.department}
                />
                {renderFieldError("subprefecture")}
              </Grid>

              <Grid item xs={12} md={6}>
                <SoftTypography component="label" sx={formFieldLabelSx}>
                  {t("establishments.col.name", "Name")}
                </SoftTypography>
                <SoftInput name="name" value={formik.values.name} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder={t("establishments.col.name", "Name")} />
                {renderFieldError("name")}
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftTypography component="label" sx={formFieldLabelSx}>
                  {t("establishments.col.code", "Code")}
                </SoftTypography>
                <SoftInput name="code" value={formik.values.code} onChange={formik.handleChange} placeholder="EC-001" />
              </Grid>
              <Grid item xs={12} md={4}>
                <SoftTypography component="label" sx={formFieldLabelSx}>
                  {t("establishments.col.type", "Type")}
                </SoftTypography>
                <SoftInput name="type" value={formik.values.type} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder="Primaire" />
                {renderFieldError("type")}
              </Grid>
              <Grid item xs={12} md={4}>
                <SoftTypography component="label" sx={formFieldLabelSx}>
                  {t("establishments.col.status", "Status")}
                </SoftTypography>
                <SoftInput name="status" value={formik.values.status} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder="Active" />
                {renderFieldError("status")}
              </Grid>
              <Grid item xs={12} md={4}>
                <SoftTypography component="label" sx={formFieldLabelSx}>
                  {t("establishments.field.creation_date", "Creation date")}
                </SoftTypography>
                <SoftInput type="date" name="creationDate" value={formik.values.creationDate} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                {renderFieldError("creationDate")}
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftTypography component="label" sx={formFieldLabelSx}>
                  {t("establishments.field.latitude", "Latitude")}
                </SoftTypography>
                <SoftInput name="latitude" value={formik.values.latitude} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                {renderFieldError("latitude")}
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftTypography component="label" sx={formFieldLabelSx}>
                  {t("establishments.field.longitude", "Longitude")}
                </SoftTypography>
                <SoftInput name="longitude" value={formik.values.longitude} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                {renderFieldError("longitude")}
              </Grid>
              <Grid item xs={12}>
                <EstablishmentMapPicker
                  active={formOpen}
                  latitude={formik.values.latitude}
                  longitude={formik.values.longitude}
                  hint={t("establishments.map_hint", "Click on the map to set the establishment location.")}
                  onLocationChange={({ latitude, longitude }) => {
                    formik.setFieldValue("latitude", latitude);
                    formik.setFieldValue("longitude", longitude);
                    formik.setFieldTouched("latitude", true);
                    formik.setFieldTouched("longitude", true);
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <SoftButton variant="outlined" color="secondary" onClick={() => { setFormOpen(false); setEditingRow(null); }}>
              {t("establishments.cancel", "Cancel")}
            </SoftButton>
            <SoftButton variant="gradient" color="info" type="submit">
              {t("establishments.save", "Save")}
            </SoftButton>
          </DialogActions>
        </form>
      </Dialog>}
    </DashboardLayout>
  );
}

export default EstablishmentsPage;
